import { IRecordCategoryNodesVisibilityRequest } from "@faire/web-api/indigofair/category/IRecordCategoryNodesVisibilityRequest";
import { IAction } from "@faire/web-api/indigofair/data/IAction";
import { IAssignSettingAction } from "@faire/web-api/indigofair/data/IAssignSettingAction";
import { ILoggingAction } from "@faire/web-api/indigofair/data/ILoggingAction";
import { IOpenModalAction } from "@faire/web-api/indigofair/data/IOpenModalAction";
import { IRecordCategoryNodesVisibilityAction } from "@faire/web-api/indigofair/data/IRecordCategoryNodesVisibilityAction";
import { IUserMilestone } from "@faire/web-api/indigofair/data/IUserMilestone";
import { trackClientEvent } from "@faire/web-api/trackClientEvent";
import { History } from "history";

import {
  openProfilePictureUploadModal,
  openSamplesRequestModal,
  openTeamMembersModal,
} from "@faire/retailer/components/App/AppModals/utils";
import { getSamplesRequestModalSourceFromDisplayPageType } from "@faire/retailer/components/SDUI/__internal__/ActionHandler/__internal__/getSamplesRequestModalSourceFromDisplayPageType";
import { LoggingEventParams } from "@faire/retailer/components/SDUI/__internal__/types";
import { assignSettingOnEvent } from "@faire/retailer/settings/assignSettingOnEvent";

import Source = IRecordCategoryNodesVisibilityRequest.Source;

type SDUIActionHandler = () => void;

const noOpSDUIActionHandler: SDUIActionHandler = () => {};

export function SDUIOnViewActionsHandler(
  actions: IAction[],
  loggingData: LoggingEventParams,
  history: History,
  recordMilestoneOnceByDay: (
    milestone: IUserMilestone.RetailerMilestone
  ) => Promise<unknown>,
  clearAllFilters: () => void,
  maybeAssignCategoryTreeExperiment: (
    getCategoryPathIdentifiers: () => (string | undefined)[],
    source?: keyof typeof Source
  ) => Promise<void>
) {
  const actionHandlers: SDUIActionHandler[] = actions.map((action) => {
    if (action.navigate) {
      return getSDUINavigateActionHandler(history, action.navigate.link);
    } else if (action.record_milestone) {
      return getSDUIRecordMilestoneHandler(
        recordMilestoneOnceByDay,
        action.record_milestone.milestone
      );
    } else if (action.open_modal) {
      return getSDUIOpenModalActionHandler(
        history,
        action.open_modal,
        loggingData
      );
    } else if (action.clear_filter) {
      return clearAllFilters;
    } else {
      return getSDUIActionHandlersForActionsWithoutState(
        action,
        maybeAssignCategoryTreeExperiment
      );
    }
  });
  actionHandlers.forEach((actionHandler) => {
    actionHandler();
  });
}

function getSDUINavigateActionHandler(
  history: History,
  link: string | undefined
): SDUIActionHandler {
  return () => {
    if (link) {
      history.push(link);
    }
  };
}

function getSDUIRecordMilestoneHandler(
  milestoneRecorder: (
    milestone: IUserMilestone.RetailerMilestone
  ) => Promise<unknown>,
  milestone: string | undefined
): SDUIActionHandler {
  return () => {
    milestoneRecorder(milestone as IUserMilestone.RetailerMilestone);
  };
}

function getSDUIActionHandlersForActionsWithoutState(
  action: IAction,
  maybeAssignCategoryTreeExperiment: (
    getCategoryPathIdentifiers: () => (string | undefined)[],
    source?: keyof typeof Source
  ) => Promise<void>
): SDUIActionHandler {
  if (action.assign_setting) {
    return getSDUIAssignSettingActionHandler(action.assign_setting);
  } else if (action.log_event) {
    return getSDUILogEventActionHandler(action.log_event);
  } else if (action.record_category_nodes_visibility) {
    return getSDUIRecordCategoryNodesVisibilityActionHandler(
      action.record_category_nodes_visibility,
      maybeAssignCategoryTreeExperiment
    );
  }
  return noOpSDUIActionHandler;
}

function getSDUIAssignSettingActionHandler(
  assignSettingAction: IAssignSettingAction
): SDUIActionHandler {
  return () => assignSettingOnEvent(assignSettingAction.setting_name);
}

function getSDUILogEventActionHandler(
  logEventAction: ILoggingAction
): SDUIActionHandler {
  return () => {
    if (!logEventAction.event_name) {
      return;
    }
    trackClientEvent({
      eventKey: logEventAction.event_name,
      parameters: logEventAction.parameters,
    });
  };
}

function getSDUIOpenModalActionHandler(
  history: History,
  openModalAction: IOpenModalAction,
  loggingData: LoggingEventParams
): SDUIActionHandler {
  if (openModalAction.open_brand_sample_modal) {
    return () =>
      openSamplesRequestModal(
        history,
        openModalAction.open_brand_sample_modal?.brand_token ?? "",
        getSamplesRequestModalSourceFromDisplayPageType(
          loggingData.view_display_page
        )
      );
  }

  if (openModalAction.open_team_member_modal) {
    return () => openTeamMembersModal(history);
  }

  if (openModalAction.open_profile_pic_upload_modal) {
    return () => openProfilePictureUploadModal(history);
  }

  return noOpSDUIActionHandler;
}

function getSDUIRecordCategoryNodesVisibilityActionHandler(
  recordCategoryNodesVisibilityAction: IRecordCategoryNodesVisibilityAction,
  maybeAssignCategoryTreeExperiment: (
    getCategoryPathIdentifiers: () => (string | undefined)[],
    source?: keyof typeof Source
  ) => Promise<void>
): SDUIActionHandler {
  return () =>
    maybeAssignCategoryTreeExperiment(
      () => recordCategoryNodesVisibilityAction.category_path_identifiers,
      recordCategoryNodesVisibilityAction.source
    );
}
